import React, { type FunctionComponent, useCallback, useState, useMemo } from 'react'
import { SimpleSelect } from '@retailer-platform/shared-components'
import { useFetchAislesForDepartmentQuery } from '__codegen__/api'
import { useDashMessage } from 'intl/intl.hooks'

interface Props {
  departmentId: string
  isDisabled?: boolean
  onChange: (selectedAisleId: string) => void
  initialValue?: string
}

const AislesSelectWithDepartmentId: FunctionComponent<Props> = ({
  departmentId,
  onChange,
  isDisabled,
  initialValue,
}) => {
  const [aisleId, setAisleId] = useState<string>(initialValue)

  const { data, loading, error } = useFetchAislesForDepartmentQuery({
    variables: { departmentId },
  })

  const defaultLabelMessage = useDashMessage('components.aislesSelect.defaultLabel')

  const aisles = data?.catalogDepartment?.aisles

  const aislesOptions = useMemo(() => {
    if (!aisles) return []

    const defaultOption = { label: defaultLabelMessage, value: undefined }
    const aisleOptions = aisles.map(aisle => ({ label: aisle.name, value: aisle.id }))

    return [defaultOption, ...aisleOptions]
  }, [aisles, defaultLabelMessage])

  const handleOnChange = useCallback(
    (selectedAisleId?: string) => {
      setAisleId(selectedAisleId)
      onChange(selectedAisleId)
    },
    [onChange]
  )

  return (
    <SimpleSelect
      loading={loading}
      error={error}
      value={aisleId}
      onChange={handleOnChange}
      options={aislesOptions}
      aria-label="aisles-select"
      isDisabled={isDisabled}
    />
  )
}

export const AislesSelect: FunctionComponent<Props> = ({ departmentId, ...rest }) => {
  const placeholder = useDashMessage('components.departmentsSelect.noDepartment')

  /* 
    Using this approach to conditionally call the useAislesSelectGetAislesQuery.
  */
  if (!departmentId) {
    return (
      <SimpleSelect
        placeholder={placeholder}
        value={null}
        isDisabled
        onChange={() => null}
        options={[]}
        aria-label="aisles-select-no-department"
      />
    )
  }

  return <AislesSelectWithDepartmentId departmentId={departmentId} {...rest} />
}
